/**
 * Map presentation state: which layers are shown, terrain exaggeration, and the
 * render profile (3D globe vs 2D offline). The Command right rail's LAYERS tab
 * writes here; the map controller (Phase 4) reads it. Kept out of droneStore
 * because none of it updates at the telemetry rate.
 */

import { create } from 'zustand';

import type { MapLod } from '@/map/aors';

/** 3D globe + terrain for connected ops; 2D flat for edge / air-gapped. */
export type MapProfile = '3d' | '2d';

/** The base imagery under everything. Dark monochrome is the resting aesthetic;
 *  satellite is opt-in for operators correlating the COP against real terrain. */
export type Basemap = 'dark' | 'satellite';

export type LayerKey =
  | 'drones'
  | 'remoteId'
  | 'adsb'
  | 'cot'
  | 'surroundings'
  | 'routesUploaded'
  | 'routesDraft'
  | 'cones'
  | 'aor'
  | 'remoteNodes'
  | 'isr';

export interface LayerMeta {
  key: LayerKey;
  label: string;
  /** A layer with no data source yet is shown but disabled, never faked on. */
  available: boolean;
}

//: `available` is a claim about whether the map obeys the switch, and it was
//: wrong in both directions: four enabled entries had no consumer at all, so
//: they toggled and changed nothing, while ADS-B and CoT ingest had shipped and
//: were still advertised as unavailable. A control that lies about what it does
//: is the same defect as the "MAV2 SIGNED" badge that claimed a signature the
//: backend never applied. `layerConsumers` below and the test that reads it keep
//: this honest.
export const LAYERS: LayerMeta[] = [
  { key: 'drones', label: 'Own aircraft', available: true },
  { key: 'routesUploaded', label: 'Routes · on aircraft', available: true },
  { key: 'routesDraft', label: 'Routes · draft', available: true },
  { key: 'aor', label: 'AOR boundary', available: true },
  { key: 'adsb', label: 'ADS-B tracks', available: true },
  { key: 'cot', label: 'CoT / TAK tracks', available: true },
  { key: 'surroundings', label: 'Reported by aircraft', available: true },
  // Nothing draws a sensor cone anywhere in this app. The entry stays so the
  // capability is visibly planned rather than quietly dropped, but it is not
  // offered as working — building a cone renderer to justify a switch would be
  // solving the problem backwards.
  { key: 'cones', label: 'Sensor cones', available: false },
  { key: 'remoteId', label: 'Remote ID tracks', available: false },
  // Other Parallax nodes. Drawn only for admins and observers, the accounts
  // the server shows another node's picture to at all.
  { key: 'remoteNodes', label: 'Other nodes · aircraft', available: true },
  { key: 'isr', label: 'Other nodes · ISR signals', available: true },
];

/**
 * Which module obeys each switch. Not used at runtime — it is the subject of
 * `mapStore.test.ts`, which fails if an `available: true` layer names a file
 * that does not read its key. The four dead toggles survived a design audit and
 * two functionality audits precisely because nothing could tell "the operator
 * turned this off" from "this never did anything".
 */
export const layerConsumers: Record<LayerKey, string | null> = {
  drones: 'src/map/useDroneMarkers.ts',
  routesUploaded: 'src/map/useMissionLayer.ts',
  routesDraft: 'src/map/useMissionLayer.ts',
  aor: 'src/map/MapCanvas.tsx',
  adsb: 'src/map/useExternalTrackLayer.tsx',
  cot: 'src/map/useExternalTrackLayer.tsx',
  surroundings: 'src/map/useSurroundingsLayer.tsx',
  cones: null,
  remoteId: null,
  remoteNodes: 'src/map/useRemoteNodeLayer.ts',
  isr: 'src/map/useRemoteNodeLayer.ts',
};

/** Where a map is looking. Persisted so switching screens keeps the view. */
export interface MapCamera {
  center: { lat: number; lon: number };
  zoom: number;
  pitch: number;
  bearing: number;
}

interface MapStoreState {
  /** Level of detail derived from map zoom; gates the strategic vs tactical HUD. */
  lod: MapLod;
  /** A fly request from outside the map (the header AOR dropdown). target is an
   *  AOR id or the literal 'ORBIT'; the nonce forces the effect to re-run. */
  flyRequest: { target: string; nonce: number };
  /** The last camera any map settled on. Command's tilted 3D view carries into
   *  Planning instead of snapping flat, so the two screens are one map. */
  camera: MapCamera | null;
  setCamera: (camera: MapCamera) => void;
  setLod: (lod: MapLod) => void;
  flyTo: (target: string) => void;
  profile: MapProfile;
  /** 3D terrain is opt-in: the DEM mesh stalls weaker GPUs, so the default is a
   *  fast flat-shaded globe. Operators enable relief from the LAYERS tab. */
  terrainOn: boolean;
  terrainExaggeration: number;
  /** Which base imagery the map shows. Read by the map controller to toggle the
   *  raster layers; the BasemapToggle overlay writes it. */
  basemap: Basemap;
  layers: Record<LayerKey, boolean>;
  setProfile: (profile: MapProfile) => void;
  setBasemap: (basemap: Basemap) => void;
  setTerrainOn: (on: boolean) => void;
  setTerrain: (value: number) => void;
  toggleLayer: (key: LayerKey) => void;
  setLayer: (key: LayerKey, on: boolean) => void;
}

export const useMapStore = create<MapStoreState>((set) => ({
  lod: 'strategic',
  flyRequest: { target: '', nonce: 0 },
  camera: null,
  setCamera: (camera) => set({ camera }),
  setLod: (lod) => set({ lod }),
  flyTo: (target) => set((state) => ({ flyRequest: { target, nonce: state.flyRequest.nonce + 1 } })),
  profile: '3d',
  terrainOn: false,
  terrainExaggeration: 1.4,
  basemap: 'dark',
  layers: {
    drones: true,
    routesUploaded: true,
    routesDraft: true,
    aor: true,
    // On, because these filter a layer that has always rendered every contact
    // it was given. Defaulting them off would make the switches "work" by
    // hiding tracks that are on the map today.
    adsb: true,
    cot: true,
    // On: these are what the aircraft itself is steering round, and before
    // this layer existed none of it was drawn at all.
    surroundings: true,
    cones: false,
    remoteId: false,
    // Aircraft on, like every other contact layer; signals off, because a busy
    // edge reports hundreds of Wi-Fi and Bluetooth emitters and they would
    // bury the aircraft until somebody asked for them.
    remoteNodes: true,
    isr: false,
  },
  setProfile: (profile) => set({ profile }),
  setBasemap: (basemap) => set({ basemap }),
  setTerrainOn: (terrainOn) => set({ terrainOn }),
  setTerrain: (terrainExaggeration) => set({ terrainExaggeration }),
  toggleLayer: (key) => set((state) => ({ layers: { ...state.layers, [key]: !state.layers[key] } })),
  setLayer: (key, on) => set((state) => ({ layers: { ...state.layers, [key]: on } })),
}));
